import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import type { SyntheticEvent } from 'react';
import React, { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useInterval, useUpdate } from 'react-use';

import NavIconNotifications from 'assets/icons/nav-icons/notifications.inline.svg';

import ClearIcon from '@mui/icons-material/Clear';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import type { PopoverActions } from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import useMaskAmount from 'app-components/hooks/privacyMode/useMaskAmount';
import { alertsActions, alertsSelectors, alertsTypes } from 'app-components/flux/alerts';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { useFetchOnce } from 'app-components/hooks/utils';
import { getSmartTimeLabel } from 'app-components/utils/date';
import { isDemoMode } from 'app-components/utils/core';

import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import ZeroStateView from 'components/ZeroStateView';
import Dump from 'components/Dump';
import { useAppSelector } from 'hooks/redux';
import { trackTopBarNavMenuClick } from 'components/TopBar/TopBarNavItems/trackTopBarNavMenuClick';

// Legacy spending alerts (created before SIMPL-33809) carry a web-style
// `/reports/...` deepLinkUri with no `parentConfigId` (e.g. `/reports/spending/by-month`),
// whose groupBy/layoutView segments no longer resolve to a valid report. Map those
// notification types to the predefined report's parentConfigId so the reports loader
// opens them. Keyed by deepLinkComponent first, then notification type. Mirrors the
// mobile mapping in getRouteForNotification.ts.
const LEGACY_SPENDING_PARENT_CONFIG_ID: Record<string, string> = {
  'spending-by-month': '11', // Spending Summary (over time)
  MONTHLY_SPENDING_COMPARISON: '11',
  'spending-by-category': '12', // Spending by Category (matches QCS parentConfigId)
  UNCATEGORIZED_TXNS: '12',
};

// True when the deep link targets the reports route but lacks a parentConfigId —
// i.e. a legacy `/reports/...` path the reports loader can't open directly.
const isReportsPathWithoutParentConfigId = (uri: string): boolean => {
  try {
    const parsed = new URL(uri, window.location.origin);
    const isReports = parsed.pathname === '/reports' || parsed.pathname.startsWith('/reports/');
    return isReports && !parsed.searchParams.get('parentConfigId');
  } catch {
    return false;
  }
};

const pathForAlert = (alert: alertsTypes.Alert): string | undefined => {
  let path = alert.deepLinkUri;

  switch (alert.deepLinkComponent) {
    case 'transactions':
      if (alert.deepLinkEntityId) {
        const tmpURL = new URL(window.location.origin);
        tmpURL.pathname = '/transactions';
        tmpURL.searchParams.append('txnId', alert.deepLinkEntityId);
        path = tmpURL.pathname + tmpURL.search;
      }
      break;
    default:
  }

  switch (alert.type) {
    case 'REFUND_TRACKER':
      path = '/upcoming/refund-tracker';
      break;
    default:
  }

  // Legacy spending alerts: rewrite an unnavigable /reports/... path to the
  // predefined report so it opens via the reports loader's parentConfigId flow.
  const legacyParentConfigId =
    LEGACY_SPENDING_PARENT_CONFIG_ID[alert.deepLinkComponent ?? ''] ??
    LEGACY_SPENDING_PARENT_CONFIG_ID[alert.type ?? ''];
  if (legacyParentConfigId && typeof path === 'string' && isReportsPathWithoutParentConfigId(path)) {
    return `/reports?parentConfigId=${legacyParentConfigId}`;
  }

  return path;
};

const useStyles = makeStyles<void, 'content' | 'listItem' | 'showOnHover' | 'hideOnHover'>()(
  (theme, _params, classes) => ({
    content: {
      minHeight: 340,
      maxHeight: 500,
      overflow: 'auto',
    },
    listItem: {
      '&:hover': {
        [`& .${classes.showOnHover}`]: {
          opacity: 1,
          visibility: 'visible',
          display: 'flex',
        },
        [`& .${classes.hideOnHover}`]: {
          opacity: 0,
          visibility: 'hidden',
          display: 'none',
        },
      },
    },
    hideOnHover: {
      opacity: 1,
      visibility: 'visible',
      display: 'flex',
    },
    showOnHover: {
      opacity: 0,
      visibility: 'hidden',
      display: 'none',
    },
  }),
);

const badgeSx = {
  '& .MuiBadge-badge': {
    minWidth: '16px !important',
    height: '16px !important',
    fontSize: '10px !important',
    paddingLeft: '4px !important',
    paddingRight: '4px !important',
  },
};

const iconBoxSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  '& > svg': {
    display: 'block',
    flexShrink: 0,
  },
} as const;

type TopBarNotificationsProps = {
  id?: string;
};

const TopBarNotifications = memo(function TopBarNotifications({ id }: TopBarNotificationsProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const onClose = useCallback(() => setOpen(false), []);

  const paperRef = useRef<HTMLDivElement>(null);
  const { classes } = useStyles();
  const alertsUnread = useAppSelector(alertsSelectors.getAlertsUnread);
  const navigate = useNavigate();
  const theme = useTheme();

  const palette = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(alertsTypes.IconBgColors).map(([_key, value], index) => [
          value,
          theme.palette.data.sequence[index % theme.palette.data.sequence.length],
        ]),
      ),
    [theme.palette.data.sequence],
  );

  const dispatch = useDispatch();
  useFetchOnce(alertsActions.getAlerts, alertsSelectors);
  const [disableClickAway, setDisableClickAway] = useState(false);
  const alerts = useAppSelector(alertsSelectors.getAlerts);
  const popoverActions = React.createRef<PopoverActions>();

  const update = useUpdate();
  useInterval(update, open ? 60000 : null);

  const onClickAway = useCallback(
    (event: React.MouseEvent) => {
      if (buttonRef.current?.contains(event.target as Node)) {
        event.preventDefault();
      }
      if (!disableClickAway) {
        onClose();
      }
    },
    [disableClickAway, onClose],
  );

  // Document-level click-outside so closing works in TopBar (Tooltip can block backdrop clicks)
  useEffect(() => {
    if (!open || disableClickAway) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (paperRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, disableClickAway, onClose]);

  const onPopperKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  // mark items as read when visible
  const [alertsRead, setAlertsRead] = useState(ImmutableSet<string>());
  const intersectionObserverHandler = useCallback<IntersectionObserverCallback>(
    (entries) =>
      entries
        .filter((entry) => entry.isIntersecting)
        // @ts-expect-error dataset does exist here
        .forEach((entry) => setAlertsRead((set) => set.add(entry.target.dataset.alertid))),
    [],
  );
  const [intersectionObserver, setIntersectionObserver] = useState<IntersectionObserver | undefined | null>();
  useEffect(() => {
    let observer: IntersectionObserver | undefined;
    if (open) {
      setTimeout(() => {
        observer = new IntersectionObserver(intersectionObserverHandler, {
          root: null,
          rootMargin: '0px',
          threshold: 0.9,
        });
        setIntersectionObserver(observer);
      }, 3000);
    }
    return () => {
      observer?.disconnect();
      setIntersectionObserver(null);
    };
  }, [open, intersectionObserverHandler]);

  const dispatchAlertsRead = useCallback(() => {
    if (alertsRead?.size) {
      const alertsUpdates = alertsRead
        .map((id) => alerts.get(id))
        .filter((alert): alert is NonNullable<typeof alert> => !!alert && !alert.isRead && !alert.isDismissed)
        .toArray()
        .map((alert) => alert.set('isRead', true));
      if (alertsUpdates.length) {
        dispatch(alertsActions.batchAlerts(alertsUpdates));
      }
    }
  }, [alertsRead, alerts, dispatch]);

  useEffect(() => {
    if (!open && !isDemoMode()) {
      dispatchAlertsRead();
    }
  }, [open, dispatchAlertsRead]);

  const maskAmount = useMaskAmount();

  return (
    <Badge max={99} badgeContent={alertsUnread.size} color='error' sx={badgeSx}>
      <IconButton
        variant='tonal'
        shape='rounded'
        id={id ?? 'topbar-notifications'}
        ref={buttonRef}
        onClick={() =>
          setOpen((prev) => {
            if (!prev) {
              trackTopBarNavMenuClick({ menuItem: 'Open', section: 'topbar-notifications' });
            }
            return !prev;
          })
        }
        size='medium'
        aria-label='Notifications'
      >
        <Box component='span' sx={iconBoxSx}>
          <NavIconNotifications width={20} height={20} />
        </Box>
      </IconButton>

      {open && buttonRef.current && (
        <Popover
          action={popoverActions}
          anchorEl={buttonRef.current}
          open={Boolean(open)}
          onClose={(_, reason) => {
            if (reason === 'backdropClick' && !disableClickAway) {
              onClose();
            }
            if (reason === 'escapeKeyDown') {
              onClose();
            }
          }}
          onKeyDown={onPopperKeyDown}
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'bottom',
          }}
          transformOrigin={{
            horizontal: 'right',
            vertical: 'top',
          }}
          slotProps={{
            paper: {
              elevation: 24,
              style: {
                width: 384,
                transform: 'translate3d(0, 0, 0)', // fixes a scroll issue: https://github.com/mui-org/@mui/material/issues/10601
                padding: 1,
              },
            },
            root: {
              onClick: onClickAway,
            },
          }}
          disableEnforceFocus
        >
          <Box ref={paperRef}>
            <Box padding={2} display='flex' justifyContent='space-between' alignItems='baseline'>
              <Typography variant='titleMedium'>Notifications</Typography>
              <Box>
                {Boolean(alerts.size) && (
                  <Button
                    id='clear-all'
                    variant='text'
                    size='small'
                    onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                      const alertsUpdates = alerts
                        .valueSeq()
                        .map((alert) =>
                          alert.merge({
                            isDismissed: true,
                            isDeleted: true,
                          }),
                        )
                        .toArray();
                      if (alertsUpdates.length > 0) {
                        setDisableClickAway(true);
                        dispatch(
                          rootUiActions.createDialog(
                            rootUiTypes.mkRootUiData({
                              id: 'clear-notifications-confirmation',
                              type: DIALOG_CONFIRMATION,
                              allowNesting: true,
                              props: ImmutableMap(
                                mkConfirmationDialogProps({
                                  title: 'Clear Notifications',
                                  content: 'Are you sure you want to clear all notifications?',
                                  confirmLabel: 'Yes',
                                  confirmAction: (_reason, closeEvent) => {
                                    dispatch(
                                      alertsActions.batchAlerts(alertsUpdates, {
                                        undo: {
                                          userMessage: `${alerts.size} notifications dismissed.`,
                                          disableUndoAction: true,
                                        },
                                      }),
                                    );
                                    onClose();
                                    closeEvent?.stopPropagation();
                                    onClose();
                                    setTimeout(() => setDisableClickAway(false), 500);
                                  },
                                  denyLabel: 'Cancel',
                                  denyAction: (_reason, closeEvent) => {
                                    closeEvent?.stopPropagation();
                                    setTimeout(() => setDisableClickAway(false), 500);
                                  },
                                }),
                              ),
                            }),
                          ),
                        );
                      }
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
            </Box>

            <Box className={classes.content}>
              {!alerts.size && (
                <ZeroStateView
                  qIllustration='notificationBellTwo'
                  size='small'
                  title='All caught up!'
                  description="You don't have any notifications."
                  button={
                    <IconButton
                      size='small'
                      tabIndex={0}
                      aria-label='keyboard hook'
                      autoFocus
                      onClick={(event: SyntheticEvent) => event.stopPropagation()}
                      sx={{ width: 0, height: 0, padding: 0 }}
                    />
                  }
                />
              )}

              {Boolean(alerts.size) && (
                <List dense disablePadding>
                  {alerts.valueSeq().map((alert, index: number) => (
                    <React.Fragment key={alert.id}>
                      <Dump value={alert} />
                      {Boolean(index) && <Divider key={`${alert.id}-divider`} variant='inset' component='li' />}
                      <ListItem disablePadding key={`${alert.id}-item`}>
                        <ListItemButton
                          data-alertid={alert.id}
                          id={`${alert.type}:${index}`}
                          key={`${alert.id}-item-button`}
                          autoFocus={!index}
                          alignItems='flex-start'
                          onClick={(event) => {
                            event.stopPropagation();
                            trackTopBarNavMenuClick({
                              menuItem: 'Notification',
                              section: 'topbar-notifications',
                              ...(alert.type ? { value: alert.type } : {}),
                            });
                            if (alert.id && !alert.isRead) {
                              trackTopBarNavMenuClick({
                                menuItem: 'Mark as read',
                                section: 'topbar-notifications',
                              });
                              dispatch(alertsActions.updateAlert(alert.set('isRead', true)));
                            }
                            if (alert.deepLinkUri?.length) {
                              const path = pathForAlert(alert);
                              if (path) {
                                navigate(path);
                              }
                            }
                            onClose();
                          }}
                          className={classes.listItem}
                          ref={(itemRef) => {
                            if (itemRef) {
                              intersectionObserver?.observe(itemRef);
                            }
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 42 }}>
                            <Avatar
                              variant='rounded'
                              alt={alert.title}
                              src={alert.icon}
                              sx={{
                                '& svg': {
                                  filter: 'brightness(0) invert(1)', // make it white
                                },
                                width: 32,
                                height: 32,
                                padding: 0.8,
                                border: 0,
                                backgroundColor:
                                  (alert.iconBgColor != null ? palette[alert.iconBgColor] : undefined) ||
                                  theme.palette.data.sequence[
                                    (alert.title?.charCodeAt(0) ?? 0) % theme.palette.data.sequence.length
                                  ],
                              }}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={<span data-testid='notification-title'>{alert.title}</span>}
                            secondary={
                              <span
                                data-testid='notification-message'
                                dangerouslySetInnerHTML={{ __html: maskAmount(alert.message ?? '') }}
                              />
                            }
                            slotProps={{
                              primary: {
                                variant: 'paragraphMedium',
                              },
                              secondary: {
                                variant: 'captionRegular',
                              },
                            }}
                          />

                          <Box minWidth={72} display='flex' justifyContent='flex-end' paddingY={2} paddingLeft={1}>
                            <Stack
                              gap={1}
                              className={classes.hideOnHover}
                              alignItems='center'
                              sx={{ transition: 'opacity 120ms ease', pointerEvents: 'none' }}
                            >
                              <Badge color='error' variant='dot' invisible={alert.isRead} />

                              <Typography variant='captionRegular' color='text.secondary'>
                                {getSmartTimeLabel(alert.createdAt)}
                              </Typography>
                            </Stack>
                            <Box
                              className={classes.showOnHover}
                              sx={{ transition: 'opacity 120ms ease, visibility 120ms ease' }}
                            >
                              <Tooltip title='Remove'>
                                <IconButton
                                  size='small'
                                  aria-label='delete'
                                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                    event.stopPropagation();
                                    dispatch(alertsActions.deleteAlert(alert.set('isDeleted', true)));
                                  }}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Popover>
      )}
    </Badge>
  );
});

export default TopBarNotifications;
